import { supabase } from "@/integrations/supabase/client";
import type { PropertyOffer } from "@/types/buyer";

export const propertyOffersService = {
  /**
   * Submit a new offer on a property
   */
  async submitOffer(
    propertyId: string,
    offerAmount: number,
    closeDate: string,
    buyerNotes?: string
  ) {
    const { data: { session } } = await supabase.auth.getSession();
    
    return await supabase.functions.invoke('submit-property-offer', {
      body: {
        propertyId,
        offerAmount,
        closeDate,
        buyerNotes
      },
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    });
  },

  /**
   * Withdraw a pending offer
   */
  async withdrawOffer(offerId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    return await supabase.functions.invoke('update-property-offer-status', {
      body: {
        offerId,
        action: 'withdraw'
      },
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    });
  },

  /**
   * Accept an offer (seller action)
   */
  async acceptOffer(offerId: string, sellerResponse?: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    return await supabase.functions.invoke('update-property-offer-status', {
      body: {
        offerId,
        action: 'accept',
        sellerResponse
      },
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    });
  },

  /**
   * Reject an offer (seller action)
   */
  async rejectOffer(offerId: string, sellerResponse?: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    return await supabase.functions.invoke('update-property-offer-status', {
      body: {
        offerId,
        action: 'reject',
        sellerResponse
      },
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    });
  },

  /**
   * Get a single offer by ID
   */
  /**
   * SECURITY NOTE: buyer_profiles queries intentionally select only 
   * business-relevant columns. Sensitive data (addresses, Stripe IDs, 
   * subscription status) is excluded to prevent PII exposure to sellers.
   */
  async getOfferById(offerId: string): Promise<PropertyOffer | null> {
    const { data, error } = await (supabase as any)
      .from('property_offers')
      .select(`
        *,
        property:properties(*),
        buyer_profile:buyer_profiles(id, full_name, company_name, phone_number, profile_image_url, deals_annually, average_close_time, preferred_markets, purchasing_type)
      `)
      .eq('id', offerId)
      .single();

    if (error) {
      console.error('Error fetching offer:', error);
      return null;
    }

    return data as PropertyOffer;
  },

  /**
   * Get all offers for a specific property
   */
  async getOffersForProperty(propertyId: string): Promise<PropertyOffer[]> {
    const { data, error } = await (supabase as any)
      .from('property_offers')
      .select(`
        *,
        buyer_profile:buyer_profiles(id, full_name, company_name, phone_number, profile_image_url, deals_annually, average_close_time, preferred_markets, purchasing_type)
      `)
      .eq('property_id', propertyId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching property offers:', error);
      return [];
    }

    return data as PropertyOffer[];
  },

  /**
   * Get all offers made by a buyer
   */
  async getOffersForBuyer(buyerId: string): Promise<PropertyOffer[]> {
    const { data, error } = await (supabase as any)
      .from('property_offers')
      .select(`
        *,
        property:properties(*)
      `)
      .eq('buyer_id', buyerId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching buyer offers:', error);
      return [];
    }

    return data as PropertyOffer[];
  },

  /**
   * Get all offers received by a seller
   */
  async getOffersForSeller(sellerId: string): Promise<PropertyOffer[]> {
    const { data, error } = await (supabase as any)
      .from('property_offers')
      .select(`
        *,
        property:properties(*),
        buyer_profile:buyer_profiles(id, full_name, company_name, phone_number, profile_image_url, deals_annually, average_close_time, preferred_markets, purchasing_type)
      `)
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching seller offers:', error);
      return [];
    }

    return data as PropertyOffer[];
  },
};
